/**
 * Staff Guide: Viewing Your Schedule
 * Simple one-page printable PDF guide
 */
import { Printer, Calendar, CheckCircle, Eye } from 'lucide-react';

export default function StaffScheduleGuide() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-white">
        {/* Header with Print Button */}
        <div className="no-print bg-purple-700 text-white py-4 px-6 flex justify-between items-center sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6" />
            <div>
              <h1 className="font-bold text-lg">Schedule Guide</h1>
              <p className="text-purple-100 text-sm">View Your Work Schedule</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-white text-purple-700 px-4 py-2 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Download PDF
          </button>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-8">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b-2 border-purple-600">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Schedule</h1>
            <p className="text-xl text-purple-700">How to View Your Work Shifts</p>
            <p className="text-gray-500 mt-2">Bulaccino Cafe - Nadi, Fiji</p>
          </div>

          {/* Steps */}
          <div className="space-y-6 mb-8">
            {/* Step 1 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Go to the Staff Portal</h3>
                <p className="text-gray-600 mb-2">Open your phone browser and go to:</p>
                <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-purple-700">bulaccinocafe.com/staff</p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Log In With Your PIN</h3>
                <p className="text-gray-600">Enter your 6-digit staff PIN to access your dashboard.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Tap "My Schedule"</h3>
                <p className="text-gray-600 mb-3">Find the Schedule section on your dashboard or tap the calendar icon.</p>
                <div className="bg-gray-100 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="font-semibold text-gray-700">My Schedule</span>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">4</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">View Your Shifts</h3>
                <p className="text-gray-600 mb-3">You'll see your upcoming shifts for the week:</p>
                
                {/* Example Schedule */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-purple-100 px-4 py-2 font-semibold text-purple-800">This Week</div>
                  <div className="divide-y divide-gray-100">
                    <div className="px-4 py-3 flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-800">Monday</p>
                        <p className="text-sm text-gray-500">June 24</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">7:00 AM - 3:00 PM</p>
                        <p className="text-sm text-gray-500">8 hours</p>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-800">Tuesday</p>
                        <p className="text-sm text-gray-500">June 25</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-400">OFF</p>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-800">Wednesday</p>
                        <p className="text-sm text-gray-500">June 26</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">10:00 AM - 6:00 PM</p>
                        <p className="text-sm text-gray-500">8 hours</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
            <h2 className="font-bold text-purple-800 text-lg mb-4">Good to Know</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Check your schedule at the <strong>start of each week</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Schedules are usually posted by <strong>Friday</strong> for the next week</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">If you see a problem with your schedule, <strong>tell your manager immediately</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Need a day off? See the <strong>Time Off Request Guide</strong></span>
              </li>
            </ul>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
            <p>Bulaccino Cafe - Nadi, Fiji</p>
            <p>Questions? Ask your manager or supervisor.</p>
          </div>
        </div>
      </div>
    </>
  );
}
